import { environment } from './environments/environment';
import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from "@angular/core";
import { bootstrapApplication, BrowserModule } from "@angular/platform-browser";
import { playerFactory } from "./app/app.module";
import fontawesome from '@fortawesome/fontawesome';
import solid from '@fortawesome/fontawesome-free-solid';
import regular from '@fortawesome/fontawesome-free-regular';
import brands from '@fortawesome/fontawesome-free-brands';
import { AdminApiService } from '@shared/services/admin/admin-api.service';
import { AdminIntegrationsApiService } from '@shared/services/admin/admin-integrations-api.service';
import { AdminBotsApiService } from '@shared/services/admin/admin-bots-api.service';
import { IntegrationsApiService } from '@shared/services/integrations/integrations-api.service';
import { WidgetApiService } from '@shared/services/integrations/widget-api.service';
import { ScalarClientApiService } from '@shared/services/scalar/scalar-client-api.service';
import { ScalarServerApiService } from '@shared/services/scalar/scalar-server-api.service';
import { NameService } from '@shared/services/name.service';
import { AdminAppserviceApiService } from '@shared/services/admin/admin-appservice-api.service';
import { AdminNebApiService } from '@shared/services/admin/admin-neb-api.service';
import { AdminUpstreamApiService } from '@shared/services/admin/admin-upstream-api.service';
import { AdminIrcApiService } from '@shared/services/admin/admin-irc-api.service';
import { IrcApiService } from '@shared/services/integrations/irc-api.service';
import { AdminStickersApiService } from '@shared/services/admin/admin-stickers-api-service';
import { MediaService } from '@shared/services/media.service';
import { StickerApiService } from '@shared/services/integrations/sticker-api.service';
import { BigBlueButtonApiService } from '@shared/services/integrations/bigbluebutton-api.service';
import { AdminTelegramApiService } from '@shared/services/admin/admin-telegram-api.service';
import { TelegramApiService } from '@shared/services/integrations/telegram-api.service';
import { AdminWebhooksApiService } from '@shared/services/admin/admin-webhooks-api.service';
import { WebhooksApiService } from '@shared/services/integrations/webhooks-api.service';
import { AdminCustomSimpleBotsApiService } from '@shared/services/admin/admin-custom-simple-bots-api.service';
import { SlackApiService } from '@shared/services/integrations/slack-api.service';
import { AdminSlackApiService } from '@shared/services/admin/admin-slack-api.service';
import { ToasterService } from '@shared/services/toaster.service';
import { AdminTermsApiService } from '@shared/services/admin/admin-terms-api.service';
import { AdminHookshotGithubApiService } from '@shared/services/admin/admin-hookshot-github-api.service';
import { HookshotGithubApiService } from '@shared/services/integrations/hookshot-github-api.service';
import { AdminHookshotJiraApiService } from '@shared/services/admin/admin-hookshot-jira-api.service';
import { HookshotJiraApiService } from '@shared/services/integrations/hookshot-jira-api.service';
import { AdminHookshotWebhookApiService } from '@shared/services/admin/admin-hookshot-webhook-api.service';
import { HookshotWebhookApiService } from '@shared/services/integrations/hookshot-webhook-api.service';
import { LogService } from '@shared/services/log.service';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideTranslateService } from '@ngx-translate/core';
import { provideLottieOptions } from 'ngx-lottie';
import { FormsModule } from '@angular/forms';
import { routing } from './app/app.routing';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { UiSwitchModule } from 'ngx-ui-switch';
import { ToastrModule } from 'ngx-toastr';
import { MatTabsModule } from '@angular/material/tabs';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DBConfig, NgxIndexedDBModule } from 'ngx-indexed-db';
import { AppComponent } from './app/app.component';

const dbConfig: DBConfig = {
    name: 'sticker_pack',
    version: 3,
    objectStoresMeta: [{
        store: 'packs',
        storeConfig: { keyPath: 'id', autoIncrement: false },
        storeSchema: [
            { name: 'author', keypath: 'author', options: { unique: false } },
            { name: 'avatarUrl', keypath: 'avatarUrl', options: { unique: false } },
            { name: 'description', keypath: 'description', options: { unique: false } },
            { name: 'displayName', keypath: 'displayName', options: { unique: false } },
            { name: 'id', keypath: 'id', options: { unique: true } },
            { name: 'isAnimated', keypath: 'isAnimated', options: { unique: false } },
            { name: 'isEnabled', keypath: 'isEnabled', options: { unique: false } },
            { name: 'isSelected', keypath: 'isSelected', options: { unique: false } },
            { name: 'license', keypath: 'license', options: { unique: false } },
            { name: 'stickers', keypath: 'stickers', options: { unique: false } },
            { name: 'trackingRoomAlias', keypath: 'trackingRoomAlias', options: { unique: false } },
            { name: 'animationOpts', keypath: 'animationOpts', options: { unique: false, default: null } },
        ]
    }, {
        store: 'stickers',
        storeConfig: { keyPath: 'id', autoIncrement: false },
        storeSchema: [
            { name: 'id', keypath: 'id', options: { unique: true } },
            { name: 'animationOpts', keypath: 'animationOpts', options: { unique: false, default: null } },
        ]
    }]
};


fontawesome.library.add(regular, solid, brands);

// depending on the env mode, enable prod mode or add debugging modules
if (environment.production) {
    enableProdMode();
}

if (environment.enableVConsole) {
    import('vconsole').then(module => {
        const vConsole = module.default;
        new vConsole();
    });
}

export function main() {
    return bootstrapApplication(AppComponent, {
        providers: [
            provideZoneChangeDetection({ eventCoalescing: true }),
            importProvidersFrom(BrowserModule, FormsModule, routing, NgbModule, UiSwitchModule, ToastrModule.forRoot(), MatTabsModule, CKEditorModule, FontAwesomeModule, ScrollingModule, NgxIndexedDBModule.forRoot(dbConfig)),
            AdminApiService,
            AdminIntegrationsApiService,
            AdminBotsApiService,
            IntegrationsApiService,
            WidgetApiService,
            ScalarClientApiService,
            ScalarServerApiService,
            NameService,
            AdminAppserviceApiService,
            AdminNebApiService,
            AdminUpstreamApiService,
            AdminIrcApiService,
            IrcApiService,
            AdminStickersApiService,
            MediaService,
            StickerApiService,
            BigBlueButtonApiService,
            AdminTelegramApiService,
            TelegramApiService,
            AdminWebhooksApiService,
            WebhooksApiService,
            AdminCustomSimpleBotsApiService,
            SlackApiService,
            AdminSlackApiService,
            ToasterService,
            AdminTermsApiService,
            AdminHookshotGithubApiService,
            HookshotGithubApiService,
            AdminHookshotJiraApiService,
            HookshotJiraApiService,
            AdminHookshotWebhookApiService,
            HookshotWebhookApiService,
            { provide: Window, useValue: window },
            LogService,
            provideHttpClient(withXhr(), withInterceptorsFromDi()),
            provideTranslateHttpLoader(),
            provideTranslateService({ lang: 'en', fallbackLang: 'en' }),
            provideLottieOptions({ player: playerFactory })
        ]
    });
}

if (document.readyState === "complete") {
    main();
} else {
    document.addEventListener("DOMContentLoaded", main);
}

(<any>String.prototype).hashCode = function () {
    let hash = 0, i, chr;
    if (this.length === 0) return hash;
    for (i = 0; i < this.length; i++) {
        chr = this.charCodeAt(i);
        hash = ((hash << 5) - hash) + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
};

// HACK: Work around .opener not being available
if (!window.opener && window.parent) window.opener = window.parent;
